import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useCallback, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useIntl } from 'react-intl';
import { useMatch, useNavigate } from 'react-router';

import { useGetSubscriptionByIdQuery } from 'src/apiClients';
import { usePageFilter } from 'src/contexts/page-filter-context';
import { defaultGlobalFilters } from 'src/filters/entity-filter-registry';
import { ROUTES } from 'src/utils/strings';

import { SearchFilterDialog } from './SearchFilterDialog';
import { SearchResultList } from './SearchResultList';
import { i18n } from './TopBarSearch.i18n';
import { useStyles } from './TopBarSearch.styles';
import type { GlobalSearchFilters, TopBarSearchResult } from './use-top-bar-search';
import { useTopBarSearch } from './use-top-bar-search';

export const TopBarSearch = () => {
    const intl = useIntl();
    const { classes } = useStyles();
    const navigate = useNavigate();

    const instanceMatch = useMatch(`${ROUTES.INSTANCE_DETAIL}`);
    const instanceId = instanceMatch?.params.id;
    const { currentData: instanceSubscription } = useGetSubscriptionByIdQuery(instanceId ?? '', {
        skip: !instanceId,
    });
    const tenantId = instanceSubscription?.tenantId;

    const [input, setInput] = useState('');
    const [focused, setFocused] = useState(false);
    const [activeKey, setActiveKey] = useState<string | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [globalFilters, setGlobalFilters] = useState<GlobalSearchFilters>(defaultGlobalFilters);

    const { registrations } = usePageFilter();

    // Drives the filter-icon highlight: the number of page filters narrowed below "all".
    const activeFilterCount = useMemo(
        () =>
            registrations.reduce(
                (count, registration) =>
                    count +
                    registration.columns.filter((col) => {
                        const selected = registration.filters[col.field];
                        return (
                            selected !== undefined && selected.length !== col.filterOptions.length
                        );
                    }).length,
                0,
            ),
        [registrations],
    );

    const { groups, flatResults, isFetching, hasQuery, totalResults } = useTopBarSearch(
        input,
        tenantId,
        instanceId,
        globalFilters,
    );

    const trimmedInput = input.trim();
    const isOpen = focused && trimmedInput.length > 0;

    const indexByKey = useMemo(() => {
        const map = new Map<string, number>();
        flatResults.forEach((result, index) => map.set(result.key, index));
        return map;
    }, [flatResults]);

    // Track the highlight by result key, not by raw index: the six queries
    // resolve at different times and reorder `flatResults` under us, so an index
    // would silently point at a different row. Resolve the index from the key,
    // falling back to the first result when the keyed row has disappeared.
    const fallbackIndex = totalResults > 0 ? 0 : -1;
    const activeIndex =
        activeKey !== null && indexByKey.has(activeKey)
            ? indexByKey.get(activeKey)!
            : fallbackIndex;

    const close = useCallback(() => setFocused(false), []);

    const handleFilterOpen = useCallback(() => setFilterModalOpen(true), []);
    const handleFilterClose = useCallback(() => setFilterModalOpen(false), []);

    const selectResult = useCallback(
        (result: TopBarSearchResult | undefined) => {
            if (!result) return;
            setInput('');
            setActiveKey(null);
            close();
            const navState =
                result.scrollTo || result.highlightId
                    ? { scrollTo: result.scrollTo, highlightId: result.highlightId }
                    : undefined;
            navigate(result.to, { state: navState ?? null });
        },
        [navigate, close],
    );

    const handleActiveIndexChange = useCallback(
        (index: number) => setActiveKey(flatResults[index]?.key ?? null),
        [flatResults],
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Escape') {
                setInput('');
                close();
                return;
            }
            if (!isOpen || totalResults === 0) return;
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActiveKey(flatResults[(activeIndex + 1) % totalResults]?.key ?? null);
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveKey(
                    flatResults[(activeIndex - 1 + totalResults) % totalResults]?.key ?? null,
                );
            } else if (event.key === 'Enter') {
                event.preventDefault();
                selectResult(flatResults[activeIndex]);
            }
        },
        [isOpen, totalResults, flatResults, activeIndex, selectResult, close],
    );

    return (
        <>
            <ClickAwayListener onClickAway={close}>
                <Box className={classes.root} ref={setAnchorEl} automation-id="top-bar-search">
                    <TextField
                        value={input}
                        onChange={(e) => {
                            setInput(e.target.value);
                            setFocused(true);
                        }}
                        onFocus={() => setFocused(true)}
                        onClick={() => setFocused(true)}
                        onKeyDown={handleKeyDown}
                        placeholder={intl.formatMessage(i18n.placeholder)}
                        size="small"
                        className={classes.input}
                        automation-id="top-bar-search-input"
                        slotProps={{
                            htmlInput: { 'aria-label': intl.formatMessage(i18n.ariaLabel) },
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment:
                                    registrations.length > 0 || input ? (
                                        <InputAdornment position="end">
                                            {registrations.length > 0 && (
                                                <Tooltip title={intl.formatMessage(i18n.filters)}>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={intl.formatMessage(
                                                            i18n.filters,
                                                        )}
                                                        automation-id="top-bar-search-filter"
                                                        onClick={handleFilterOpen}
                                                        color={
                                                            activeFilterCount > 0
                                                                ? 'primary'
                                                                : 'default'
                                                        }
                                                    >
                                                        <FilterListIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {input && (
                                                <IconButton
                                                    size="small"
                                                    aria-label={intl.formatMessage(i18n.clear)}
                                                    automation-id="top-bar-search-clear"
                                                    onClick={() => {
                                                        setInput('');
                                                        setActiveKey(null);
                                                    }}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    ) : null,
                            },
                        }}
                    />
                    <SearchResultList
                        open={isOpen}
                        anchorEl={anchorEl}
                        input={input}
                        groups={groups}
                        indexByKey={indexByKey}
                        activeIndex={activeIndex}
                        isFetching={isFetching}
                        hasQuery={hasQuery}
                        totalResults={totalResults}
                        onSelectResult={selectResult}
                        onActiveIndexChange={handleActiveIndexChange}
                    />
                </Box>
            </ClickAwayListener>
            <SearchFilterDialog
                open={filterModalOpen}
                onClose={handleFilterClose}
                globalFilters={globalFilters}
                onGlobalFiltersChange={setGlobalFilters}
            />
        </>
    );
};
